import { useState, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { motion } from 'framer-motion';
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Cell,
} from 'recharts';
import {
  Wallet, ChevronLeft, LayoutDashboard, ArrowLeftRight,
  FileBarChart2, BarChart3, Loader2, CheckCircle2, Pencil, X,
  ToggleLeft, ToggleRight, KeyRound, AlertCircle, Copy, ClipboardCheck,
} from 'lucide-react';
import { api } from '../lib/api';
import { formatDate, formatDateTime } from '../lib/formatters';

const inputCls = 'w-full bg-[#0b1120] border border-white/10 rounded-xl px-3 py-2.5 text-sm text-white focus:outline-none focus:border-cyan-500/40 transition-colors placeholder-slate-700';
const selectCls = inputCls + ' cursor-pointer';

const TOOLTIP_STYLE: React.CSSProperties = {
  background: '#0d1526',
  border: '1px solid rgba(255,255,255,0.08)',
  borderRadius: '10px',
  fontSize: '11px',
  color: '#e2e8f0',
};

type Tab = 'overview' | 'convert' | 'reports' | 'dashboard';

interface HolderBalance {
  worldId: string;
  currency: string;
  balance: string;
  updatedAt: string;
}

interface Holder {
  id: string;
  name: string;
  email: string;
  worldIds: string[];
  isActive: boolean;
  createdAt: string;
  balances: HolderBalance[];
}

interface World {
  id: string;
  name: string;
  currencySymbol: string;
  status: string;
}

/* ─── Edit Modal ─── */
function EditHolderModal({ holder, onClose, onSaved }: { holder: Holder; onClose: () => void; onSaved: (h: Holder) => void }) {
  const qc = useQueryClient();
  const [name, setName] = useState(holder.name);
  const [email, setEmail] = useState(holder.email);
  const [saved, setSaved] = useState(false);

  const mutation = useMutation({
    mutationFn: (data: { name: string; email: string }) =>
      api.patch(`/holders/${holder.id}`, data).then((r) => r.data),
    onSuccess: (updated: Holder) => {
      setSaved(true);
      qc.invalidateQueries({ queryKey: ['holder', holder.id] });
      qc.invalidateQueries({ queryKey: ['holders'] });
      onSaved(updated);
      setTimeout(onClose, 1200);
    },
  });

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
        className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={onClose} />
      <motion.div
        initial={{ opacity: 0, scale: 0.95, y: 12 }} animate={{ opacity: 1, scale: 1, y: 0 }}
        exit={{ opacity: 0, scale: 0.95, y: 12 }} transition={{ type: 'spring', stiffness: 380, damping: 30 }}
        className="relative w-full max-w-md glass-card p-6 z-10" style={{ borderColor: 'rgba(0,212,255,0.2)' }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="absolute top-0 left-0 right-0 h-px rounded-t-2xl bg-gradient-to-r from-transparent via-cyan-500/40 to-transparent" />
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-2.5">
            <div className="w-8 h-8 rounded-xl bg-cyan-500/10 border border-cyan-500/20 flex items-center justify-center">
              <Pencil className="w-4 h-4 text-cyan-400" />
            </div>
            <h3 className="text-sm font-semibold text-white">Edit Holder</h3>
          </div>
          <button onClick={onClose} className="text-slate-600 hover:text-slate-300 transition-colors"><X className="w-4 h-4" /></button>
        </div>
        {saved ? (
          <div className="flex items-center gap-2 text-emerald-400 text-sm"><CheckCircle2 className="w-4 h-4" /> Changes saved</div>
        ) : (
          <form onSubmit={(e) => { e.preventDefault(); mutation.mutate({ name: name.trim(), email: email.trim().toLowerCase() }); }} className="space-y-4">
            <div>
              <label className="block text-[10px] font-medium text-slate-500 uppercase tracking-wider mb-1.5">Full Name</label>
              <input value={name} onChange={(e) => setName(e.target.value)} required minLength={2} maxLength={100} className={inputCls} />
            </div>
            <div>
              <label className="block text-[10px] font-medium text-slate-500 uppercase tracking-wider mb-1.5">Email</label>
              <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} required className={inputCls} />
            </div>
            {mutation.isError && (
              <p className="text-red-400 text-xs bg-red-500/10 border border-red-500/20 rounded-xl px-3 py-2">
                {(mutation.error as { response?: { data?: { message?: string } } })?.response?.data?.message ?? 'Update failed.'}
              </p>
            )}
            <div className="flex gap-3 pt-2">
              <button type="button" onClick={onClose} className="flex-1 py-2.5 rounded-xl text-sm text-slate-400 border border-white/8 hover:border-white/15 transition-all">Cancel</button>
              <button type="submit" disabled={mutation.isPending} className="flex-1 btn-primary flex items-center justify-center gap-2 py-2.5 rounded-xl text-sm font-semibold">
                {mutation.isPending ? <><Loader2 className="w-3.5 h-3.5 animate-spin" /> Saving…</> : <><Pencil className="w-3.5 h-3.5" /> Save</>}
              </button>
            </div>
          </form>
        )}
      </motion.div>
    </div>
  );
}

/* ─── Reset Password Modal ─── */
function ResetPasswordModal({ holder, onClose }: { holder: Holder; onClose: () => void }) {
  const [result, setResult] = useState<{ resetToken: string; expiresAt: string } | null>(null);
  const [copied, setCopied] = useState(false);

  const mutation = useMutation({
    mutationFn: () => api.post(`/holders/${holder.id}/reset-password`).then((r) => r.data),
    onSuccess: (data) => setResult(data),
  });

  const copyToken = useCallback(() => {
    if (!result?.resetToken) return;
    navigator.clipboard.writeText(result.resetToken).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2500);
    });
  }, [result?.resetToken]);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
        className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={onClose} />
      <motion.div
        initial={{ opacity: 0, scale: 0.95, y: 12 }} animate={{ opacity: 1, scale: 1, y: 0 }}
        exit={{ opacity: 0, scale: 0.95, y: 12 }} transition={{ type: 'spring', stiffness: 380, damping: 30 }}
        className="relative w-full max-w-md glass-card p-6 z-10" style={{ borderColor: 'rgba(245,158,11,0.2)' }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="absolute top-0 left-0 right-0 h-px rounded-t-2xl bg-gradient-to-r from-transparent via-amber-500/40 to-transparent" />
        <div className="flex items-center justify-between mb-5">
          <div className="flex items-center gap-2.5">
            <div className="w-8 h-8 rounded-xl bg-amber-500/10 border border-amber-500/20 flex items-center justify-center">
              <KeyRound className="w-4 h-4 text-amber-400" />
            </div>
            <div>
              <h3 className="text-sm font-semibold text-white">Reset Password</h3>
              <p className="text-[10px] text-slate-600">{holder.email}</p>
            </div>
          </div>
          <button onClick={onClose} className="text-slate-600 hover:text-slate-300 transition-colors"><X className="w-4 h-4" /></button>
        </div>
        {result ? (
          <div className="space-y-4">
            <div className="flex items-center gap-2 text-emerald-400 text-sm font-medium"><CheckCircle2 className="w-4 h-4" /> Reset link generated</div>
            <div className="p-3 rounded-xl bg-white/[0.03] border border-white/[0.06] space-y-2">
              <div className="flex items-center justify-between">
                <p className="text-[10px] text-slate-500 uppercase tracking-wider">Reset Token</p>
                <button
                  onClick={copyToken}
                  className="flex items-center gap-1.5 px-2 py-1 rounded-lg text-[10px] font-medium transition-all border border-white/10 hover:border-cyan-500/30 text-slate-500 hover:text-cyan-400"
                >
                  {copied ? <><ClipboardCheck className="w-3 h-3" /> Copied!</> : <><Copy className="w-3 h-3" /> Copy</>}
                </button>
              </div>
              <p className="font-mono text-[11px] text-cyan-400 break-all select-all">{result.resetToken}</p>
              <p className="text-[10px] text-slate-600">Expires: {formatDateTime(result.expiresAt)}</p>
            </div>
            <p className="text-[11px] text-slate-600">Share this token with the holder via a secure channel. They use it at <span className="font-mono text-slate-400">POST /holders/set-password</span>.</p>
            <button onClick={onClose} className="w-full btn-primary py-2.5 rounded-xl text-sm font-semibold">Done</button>
          </div>
        ) : (
          <div className="space-y-4">
            <div className="flex items-start gap-2.5 p-3 rounded-xl bg-amber-500/[0.06] border border-amber-500/15">
              <AlertCircle className="w-4 h-4 text-amber-400 flex-shrink-0 mt-0.5" />
              <p className="text-xs text-slate-400">This will generate a 24-hour password reset token for <span className="text-white font-medium">{holder.name}</span>. The token will be sent to <span className="font-mono text-slate-300">{holder.email}</span>.</p>
            </div>
            {mutation.isError && (
              <p className="text-red-400 text-xs bg-red-500/10 border border-red-500/20 rounded-xl px-3 py-2">Failed to generate reset token.</p>
            )}
            <div className="flex gap-3">
              <button onClick={onClose} className="flex-1 py-2.5 rounded-xl text-sm text-slate-400 border border-white/8 hover:border-white/15 transition-all">Cancel</button>
              <button onClick={() => mutation.mutate()} disabled={mutation.isPending}
                className="flex-1 flex items-center justify-center gap-2 py-2.5 rounded-xl text-sm font-semibold bg-amber-500/20 border border-amber-500/30 text-amber-300 hover:bg-amber-500/30 transition-all">
                {mutation.isPending ? <><Loader2 className="w-3.5 h-3.5 animate-spin" /> Generating…</> : <><KeyRound className="w-3.5 h-3.5" /> Generate Reset Link</>}
              </button>
            </div>
          </div>
        )}
      </motion.div>
    </div>
  );
}

/* ─── Stat Card ─── */
function StatCard({ label, value, sub, accent = 'cyan' }: { label: string; value: string; sub?: string; accent?: 'emerald' | 'cyan' | 'purple' }) {
  const ring = accent === 'emerald' ? 'border-emerald-500/20 bg-emerald-500/5' : accent === 'purple' ? 'border-purple-500/20 bg-purple-500/5' : 'border-cyan-500/20 bg-cyan-500/5';
  const valColor = accent === 'emerald' ? 'text-emerald-400' : accent === 'purple' ? 'text-purple-400' : 'text-cyan-400';
  return (
    <div className={`glass-card p-4 border ${ring}`}>
      <p className="text-[10px] font-semibold uppercase tracking-widest text-slate-600 mb-2">{label}</p>
      <p className={`text-xl font-bold font-mono ${valColor}`}>{value}</p>
      {sub && <p className="text-[10px] text-slate-700 mt-1">{sub}</p>}
    </div>
  );
}

function fmtAmount(amount: number): string {
  if (amount === 0) return '0';
  if (amount >= 1e6) return `${(amount / 1e6).toFixed(4)}M`;
  if (amount >= 1e3) return `${(amount / 1e3).toFixed(4)}K`;
  return amount.toLocaleString(undefined, { maximumFractionDigits: 8 });
}

function sumByCurrency(balances: HolderBalance[]): Record<string, number> {
  return balances.reduce((acc, b) => {
    acc[b.currency] = (acc[b.currency] ?? 0) + parseFloat(b.balance || '0');
    return acc;
  }, {} as Record<string, number>);
}

/* ─── Tab: Overview ─── */
function OverviewTab({ holder }: { holder: Holder }) {
  const { data: balances = [] } = useQuery<HolderBalance[]>({
    queryKey: ['holder-balances', holder.id],
    queryFn: () => api.get(`/holders/${holder.id}/balances`).then((r) => r.data),
    refetchInterval: 30_000,
  });

  const totals = sumByCurrency(balances);
  const ukcTotal = totals['UKC'] ?? 0;
  const distinctCurrencies = new Set(balances.map((b) => b.currency)).size;
  const since = formatDate(holder.createdAt);

  const { data: worlds = [] } = useQuery<World[]>({
    queryKey: ['worlds'],
    queryFn: () => api.get('/worlds').then((r) => r.data),
  });

  const worldMap = Object.fromEntries(worlds.map((w) => [w.id, w]));

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
        <StatCard label="Total UKC Balance" value={`${fmtAmount(ukcTotal)} UKC`} accent="emerald" />
        <StatCard label="Currencies Held" value={String(distinctCurrencies || '—')} accent="cyan" />
        <StatCard label="Status" value={holder.isActive ? 'Active' : 'Inactive'} accent={holder.isActive ? 'emerald' : 'purple'} />
        <StatCard label="Member Since" value={since} sub="UTC" accent="purple" />
      </div>

      {/* Holder info card */}
      <div className="glass-card p-6 space-y-4" style={{ borderColor: 'rgba(0,212,255,0.1)' }}>
        <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-cyan-500/20 to-transparent rounded-t-2xl" />
        <p className="text-xs font-semibold text-slate-400 uppercase tracking-widest">Holder Details</p>
        <div className="grid grid-cols-2 gap-x-8 gap-y-3">
          {[
            { label: 'Full Name',  value: holder.name },
            { label: 'Email',      value: holder.email },
            { label: 'Holder ID',  value: holder.id,   mono: true },
          ].map(({ label, value, mono }) => (
            <div key={label}>
              <p className="text-[10px] font-medium text-slate-600 uppercase tracking-wider mb-0.5">{label}</p>
              <p className={`text-sm text-slate-300 ${mono ? 'font-mono text-xs break-all' : ''}`}>{value}</p>
            </div>
          ))}
          <div className="col-span-2">
            <p className="text-[10px] font-medium text-slate-600 uppercase tracking-wider mb-0.5">Worlds</p>
            <div className="flex flex-wrap gap-1.5">
              {holder.worldIds.map((wid) => (
                <span key={wid} className="font-mono text-[10px] text-slate-300 bg-white/[0.04] border border-white/[0.08] px-2 py-0.5 rounded">
                  {wid.slice(0, 8)}…
                </span>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Balance breakdown — shows each partner world holding */}
      {balances.length === 0 ? (
        <div className="glass-card p-10 flex flex-col items-center justify-center text-center">
          <Wallet className="w-8 h-8 text-slate-700 mb-3" />
          <p className="text-sm font-medium text-slate-500">No balances yet</p>
          <p className="text-xs text-slate-700 mt-1">Fund this holder via a partner world to see balance records here.</p>
        </div>
      ) : (
        <div className="glass-card overflow-hidden">
          <div className="h-px bg-gradient-to-r from-transparent via-emerald-500/20 to-transparent" />
          <div className="px-5 py-3.5 border-b border-white/[0.04]">
            <p className="text-xs font-semibold text-slate-400 uppercase tracking-widest">Balance Breakdown — Holdings per Partner World</p>
          </div>
          <table className="w-full text-sm">
            <thead>
              <tr style={{ borderBottom: '1px solid rgba(255,255,255,0.04)' }}>
                {['Currency', 'Partner World', 'World ID', 'Balance', 'Last Updated'].map((h) => (
                  <th key={h} className="text-left px-5 py-3 text-[10px] font-semibold uppercase tracking-widest text-slate-600">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {balances.map((b) => {
                const world = worldMap[b.worldId];
                return (
                  <tr key={`${b.worldId}-${b.currency}`} style={{ borderBottom: '1px solid rgba(255,255,255,0.03)' }}>
                    <td className="px-5 py-3">
                      <span className="font-mono text-sm text-slate-300 bg-white/[0.04] px-2 py-0.5 rounded">{b.currency}</span>
                    </td>
                    <td className="px-5 py-3">
                      {world ? (
                        <div>
                          <p className="text-sm font-medium text-slate-200">{world.name}</p>
                          <p className="text-[10px] font-mono text-slate-600">{world.currencySymbol}</p>
                        </div>
                      ) : (
                        <span className="text-slate-600 text-xs">—</span>
                      )}
                    </td>
                    <td className="px-5 py-3 font-mono text-[10px] text-slate-600">{b.worldId.slice(0, 8)}…</td>
                    <td className="px-5 py-3 font-mono text-sm text-emerald-400 font-semibold">{parseFloat(b.balance).toLocaleString(undefined, { maximumFractionDigits: 2 })}</td>
                    <td className="px-5 py-3 text-xs text-slate-600">{formatDateTime(b.updatedAt)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

/* ─── Sub-section: World Currency ↔ UKC ─── */
function HolderWorldConvertSection({ holder, holderId }: { holder: Holder; holderId: string }) {
  const qc = useQueryClient();

  const { data: worlds = [] } = useQuery<World[]>({
    queryKey: ['worlds'],
    queryFn: () => api.get('/worlds').then((r) => r.data),
  });

  const [worldId, setWorldId] = useState('');
  const [direction, setDirection] = useState<'WORLD_TO_UKC' | 'UKC_TO_WORLD'>('WORLD_TO_UKC');
  const [amount, setAmount] = useState('');
  const [success, setSuccess] = useState<{ sendAmount: string; receiveAmount: string; sendCurrency: string; receiveCurrency: string } | null>(null);

  const { data: rate } = useQuery<{ ukcPerUnit: number } | null>({
    queryKey: ['exchange-rate', worldId],
    queryFn: () => worldId ? api.get(`/exchange/rates/${worldId}`).then((r) => r.data).catch(() => null) : Promise.resolve(null),
    enabled: !!worldId,
  });

  const selectedWorld = worlds.find((w) => w.id === worldId);

  // Determine which balance the user is spending
  const worldCurrencyBalance = holder.balances.find((b) => b.worldId === worldId && b.currency !== 'UKC');
  const ukcBalance = holder.balances.find((b) => b.worldId === worldId && b.currency === 'UKC');
  const spendBalance = direction === 'WORLD_TO_UKC' ? worldCurrencyBalance : ukcBalance;
  const spendCurrency = direction === 'WORLD_TO_UKC' ? (selectedWorld?.currencySymbol ?? '?') : 'UKC';
  const receiveCurrency = direction === 'WORLD_TO_UKC' ? 'UKC' : (selectedWorld?.currencySymbol ?? '?');

  const previewReceive = (() => {
    if (!amount || !rate) return null;
    const amt = parseFloat(amount);
    if (isNaN(amt) || amt <= 0) return null;
    return direction === 'WORLD_TO_UKC'
      ? (amt * rate.ukcPerUnit).toFixed(8)
      : (amt / rate.ukcPerUnit).toFixed(8);
  })();

  const mutation = useMutation({
    mutationFn: (data: { worldId: string; direction: string; amount: string }) =>
      api.post(`/holders/${holderId}/convert`, data).then((r) => r.data),
    onSuccess: (data) => {
      setSuccess(data);
      setAmount('');
      qc.invalidateQueries({ queryKey: ['holder', holderId] });
      qc.invalidateQueries({ queryKey: ['holder-balances', holderId] });
    },
  });

  // Only show worlds where the holder has at least one balance entry
  const holderWorldIds = [...new Set(holder.balances.map((b) => b.worldId))];

  return (
    <div className="glass-card p-6 space-y-5" style={{ borderColor: 'rgba(139,92,246,0.2)' }}>
      <div className="flex items-center gap-2.5">
        <div className="w-8 h-8 rounded-xl bg-purple-500/10 border border-purple-500/20 flex items-center justify-center">
          <ArrowLeftRight className="w-4 h-4 text-purple-400" />
        </div>
        <div>
          <p className="text-sm font-semibold text-white">World Currency ↔ UKC</p>
          <p className="text-[10px] text-slate-600">Convert between world currency and UKC within a single world</p>
        </div>
      </div>

      {success ? (
        <motion.div initial={{ opacity: 0, y: -6 }} animate={{ opacity: 1, y: 0 }} className="space-y-3">
          <div className="flex items-center gap-2 text-emerald-400 text-sm font-medium">
            <CheckCircle2 className="w-4 h-4" /> Conversion successful
          </div>
          <div className="p-3 rounded-xl bg-emerald-500/[0.06] border border-emerald-500/15 text-xs text-slate-400 space-y-1">
            <div className="flex justify-between">
              <span>Sold</span>
              <span className="font-mono text-red-400">{parseFloat(success.sendAmount).toLocaleString(undefined, { maximumFractionDigits: 8 })} {success.sendCurrency}</span>
            </div>
            <div className="flex justify-between">
              <span>Received</span>
              <span className="font-mono text-emerald-400 font-semibold">{parseFloat(success.receiveAmount).toLocaleString(undefined, { maximumFractionDigits: 8 })} {success.receiveCurrency}</span>
            </div>
          </div>
          <button onClick={() => setSuccess(null)} className="w-full btn-primary py-2.5 rounded-xl text-sm font-semibold">New Conversion</button>
        </motion.div>
      ) : (
        <form onSubmit={(e) => { e.preventDefault(); mutation.mutate({ worldId, direction, amount }); }} className="space-y-4">
          {/* World selector */}
          <div>
            <label className="block text-[10px] font-medium text-slate-500 uppercase tracking-wider mb-1.5">World</label>
            <select value={worldId} onChange={(e) => { setWorldId(e.target.value); setAmount(''); }} required className="w-full bg-[#0b1120] border border-white/10 rounded-xl px-3 py-2.5 text-sm text-white focus:outline-none focus:border-purple-500/40 transition-colors cursor-pointer">
              <option value="">Select world…</option>
              {holderWorldIds.map((wid) => {
                const w = worlds.find((x) => x.id === wid);
                return <option key={wid} value={wid}>{w ? `${w.name} (${w.currencySymbol})` : wid.slice(0, 8)}</option>;
              })}
            </select>
          </div>

          {/* Direction */}
          <div>
            <label className="block text-[10px] font-medium text-slate-500 uppercase tracking-wider mb-1.5">Direction</label>
            <div className="grid grid-cols-2 gap-2">
              {(['WORLD_TO_UKC', 'UKC_TO_WORLD'] as const).map((d) => (
                <button
                  key={d}
                  type="button"
                  onClick={() => { setDirection(d); setAmount(''); }}
                  className={`py-2.5 rounded-xl border text-xs font-semibold transition-all ${direction === d ? 'bg-purple-500/15 border-purple-500/35 text-purple-300' : 'border-white/8 text-slate-500 hover:border-white/15'}`}
                >
                  {d === 'WORLD_TO_UKC' ? `${selectedWorld?.currencySymbol ?? 'World'} → UKC` : `UKC → ${selectedWorld?.currencySymbol ?? 'World'}`}
                </button>
              ))}
            </div>
          </div>

          {/* Amount */}
          {worldId && (
            <div>
              <label className="block text-[10px] font-medium text-slate-500 uppercase tracking-wider mb-1.5">
                Amount ({spendCurrency}){' '}
                {spendBalance && <span className="text-slate-700 normal-case">— available: {parseFloat(spendBalance.balance).toLocaleString(undefined, { maximumFractionDigits: 4 })} {spendCurrency}</span>}
              </label>
              <input
                type="number"
                min="0.00000001"
                step="0.00000001"
                max={spendBalance ? parseFloat(spendBalance.balance) : undefined}
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                placeholder="e.g. 10.00"
                required
                className="w-full bg-[#0b1120] border border-white/10 rounded-xl px-3 py-2.5 text-sm text-white focus:outline-none focus:border-purple-500/40 transition-colors placeholder-slate-700"
              />
              {!rate && <p className="text-[10px] text-amber-600 mt-1">No exchange rate set for this world</p>}
            </div>
          )}

          {/* Preview */}
          {previewReceive && selectedWorld && (
            <motion.div initial={{ opacity: 0, y: -4 }} animate={{ opacity: 1, y: 0 }} className="p-3.5 rounded-xl bg-purple-500/[0.05] border border-purple-500/20 space-y-2">
              <p className="text-[10px] font-semibold text-purple-400 uppercase tracking-wider">Conversion Preview</p>
              <div className="flex items-center justify-between">
                <div className="text-center">
                  <p className="font-mono text-sm font-bold text-white">{parseFloat(amount).toFixed(4)} {spendCurrency}</p>
                  <p className="text-[10px] text-slate-600">You spend</p>
                </div>
                <ArrowLeftRight className="w-4 h-4 text-purple-500/60 flex-shrink-0" />
                <div className="text-center">
                  <p className="font-mono text-sm font-bold text-emerald-400">{parseFloat(previewReceive).toFixed(4)} {receiveCurrency}</p>
                  <p className="text-[10px] text-slate-600">You receive</p>
                </div>
              </div>
              {rate && (
                <p className="text-[10px] text-slate-600 text-center">Rate: 1 {selectedWorld.currencySymbol} = {rate.ukcPerUnit} UKC</p>
              )}
            </motion.div>
          )}

          {mutation.isError && (
            <p className="text-red-400 text-xs bg-red-500/10 border border-red-500/20 rounded-xl px-3 py-2">
              {(mutation.error as { response?: { data?: { message?: string } } })?.response?.data?.message ?? 'Conversion failed.'}
            </p>
          )}

          <button
            type="submit"
            disabled={mutation.isPending || !worldId || !rate || !amount}
            className="w-full flex items-center justify-center gap-2 py-3 rounded-xl text-sm font-semibold bg-purple-500/15 border border-purple-500/25 text-purple-300 hover:bg-purple-500/25 transition-all disabled:opacity-40"
          >
            {mutation.isPending ? (
              <><Loader2 className="w-4 h-4 animate-spin" /> Converting…</>
            ) : (
              <><ArrowLeftRight className="w-4 h-4" /> Execute Conversion</>
            )}
          </button>
        </form>
      )}
    </div>
  );
}

/* ─── Tab: Convert World Credits ─── */
function ConvertTab({ holder, holderId }: { holder: Holder; holderId: string }) {
  const qc = useQueryClient();

  const { data: worlds = [] } = useQuery<World[]>({
    queryKey: ['worlds'],
    queryFn: () => api.get('/worlds').then((r) => r.data),
  });

  // Worlds the holder has an existing balance in
  const balanceWorldIds = holder.balances.map((b) => b.worldId);

  // Composite key: "worldId:currency" — uniquely identifies a balance row
  const [sourceKey, setSourceKey] = useState(
    holder.balances.length > 0 ? `${holder.balances[0].worldId}:${holder.balances[0].currency}` : '',
  );
  const [destWorldId, setDestWorldId] = useState('');
  const [amount, setAmount] = useState('');
  const [success, setSuccess] = useState<{ receiveAmount: string; receiveCurrency: string; destWorldId: string } | null>(null);

  const [sourceWorldId, sourceCurrency] = sourceKey.includes(':')
    ? [sourceKey.split(':')[0], sourceKey.split(':').slice(1).join(':')]
    : [sourceKey, ''];

  // Rates: fetch when source or dest change
  const { data: sourceRate } = useQuery<{ ukcPerUnit: number; worldId: string } | null>({
    queryKey: ['exchange-rate', sourceWorldId],
    queryFn: () => sourceWorldId && sourceCurrency !== 'UKC'
      ? api.get(`/exchange/rates/${sourceWorldId}`).then((r) => r.data)
      : Promise.resolve(sourceWorldId ? { ukcPerUnit: 1, worldId: sourceWorldId } : null),
    enabled: !!sourceWorldId,
  });

  const { data: destRate } = useQuery<{ ukcPerUnit: number; worldId: string } | null>({
    queryKey: ['exchange-rate', destWorldId],
    queryFn: () => destWorldId ? api.get(`/exchange/rates/${destWorldId}`).then((r) => r.data) : Promise.resolve(null),
    enabled: !!destWorldId,
  });

  // Live preview of receive amount
  const previewAmount = (() => {
    if (!amount || !sourceRate || !destRate) return null;
    const send = parseFloat(amount);
    if (isNaN(send) || send <= 0) return null;
    // Formula: receive = send × (sourceRate / destRate)
    const receive = send * (sourceRate.ukcPerUnit / destRate.ukcPerUnit);
    return receive.toFixed(8);
  })();

  const sourceBalance = holder.balances.find((b) => b.worldId === sourceWorldId && b.currency === sourceCurrency);
  const sourceWorld = worlds.find((w) => w.id === sourceWorldId);
  const destWorld = worlds.find((w) => w.id === destWorldId);

  const mutation = useMutation({
    mutationFn: (data: { sourceWorldId: string; destWorldId: string; sourceCurrency: string; amount: string }) =>
      api.post(`/holders/${holderId}/exchange`, data).then((r) => r.data),
    onSuccess: (data) => {
      setSuccess({ receiveAmount: data.receiveAmount, receiveCurrency: data.receiveCurrency, destWorldId: data.destWorldId });
      setAmount('');
      qc.invalidateQueries({ queryKey: ['holder', holderId] });
      qc.invalidateQueries({ queryKey: ['holder-balances', holderId] });
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    mutation.mutate({ sourceWorldId, destWorldId, sourceCurrency, amount });
  };

  // Exclude only identity swaps: same world AND same currency as what we'd receive there
  const activeWorlds = worlds.filter((w) => w.status === 'ACTIVE' || balanceWorldIds.includes(w.id) || holder.worldIds.includes(w.id));

  return (
    <div className="max-w-lg space-y-6">
      <div className="glass-card p-6 space-y-5" style={{ borderColor: 'rgba(0,212,255,0.15)' }}>
        <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-cyan-500/30 to-transparent rounded-t-2xl" />

        <div className="flex items-center gap-2.5">
          <div className="w-8 h-8 rounded-xl bg-cyan-500/10 border border-cyan-500/20 flex items-center justify-center">
            <ArrowLeftRight className="w-4 h-4 text-cyan-400" />
          </div>
          <div>
            <p className="text-sm font-semibold text-white">Convert World Credits</p>
            <p className="text-[10px] text-slate-600">Exchange UKC between partner worlds using live exchange rates</p>
          </div>
        </div>

        {success ? (
          <motion.div initial={{ opacity: 0, y: -6 }} animate={{ opacity: 1, y: 0 }} className="space-y-3">
            <div className="flex items-center gap-2 text-emerald-400 text-sm font-medium">
              <CheckCircle2 className="w-4 h-4" />
              Exchange successful
            </div>
            <div className="p-3 rounded-xl bg-emerald-500/[0.06] border border-emerald-500/15 text-xs text-slate-400 space-y-1">
              <div className="flex justify-between">
                <span>Received</span>
                <span className="font-mono text-emerald-400 font-semibold">{parseFloat(success.receiveAmount).toLocaleString(undefined, { maximumFractionDigits: 8 })} {success.receiveCurrency}</span>
              </div>
              <div className="flex justify-between">
                <span>Destination world</span>
                <span className="text-slate-300">{worlds.find((w) => w.id === success.destWorldId)?.name ?? success.destWorldId.slice(0, 8)}</span>
              </div>
            </div>
            <button onClick={() => setSuccess(null)} className="w-full btn-primary py-2.5 rounded-xl text-sm font-semibold">New Exchange</button>
          </motion.div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Source world */}
            <div>
              <label className="block text-[10px] font-medium text-slate-500 uppercase tracking-wider mb-1.5">Source Balance (From)</label>
              <select value={sourceKey} onChange={(e) => { setSourceKey(e.target.value); setAmount(''); }} required className={selectCls}>
                <option value="">Select source balance…</option>
                {holder.balances.map((b) => {
                  const w = worlds.find((x) => x.id === b.worldId);
                  const key = `${b.worldId}:${b.currency}`;
                  return (
                    <option key={key} value={key}>
                      {w ? `${w.name} — ${parseFloat(b.balance).toLocaleString(undefined, { maximumFractionDigits: 4 })} ${b.currency}` : `${b.worldId.slice(0, 8)} — ${b.balance} ${b.currency}`}
                    </option>
                  );
                })}
              </select>
              {sourceWorld && sourceRate && (
                <p className="text-[10px] text-slate-600 mt-1">Rate: 1 UKC = {(1 / sourceRate.ukcPerUnit).toFixed(6)} {sourceWorld.currencySymbol} &nbsp;|&nbsp; 1 {sourceWorld.currencySymbol} = {sourceRate.ukcPerUnit} UKC</p>
              )}
              {sourceWorldId && !sourceRate && (
                <p className="text-[10px] text-amber-600 mt-1">No exchange rate set for this world</p>
              )}
            </div>

            {/* Dest world */}
            <div>
              <label className="block text-[10px] font-medium text-slate-500 uppercase tracking-wider mb-1.5">Destination World (To)</label>
              <select value={destWorldId} onChange={(e) => setDestWorldId(e.target.value)} required className={selectCls}>
                <option value="">Select destination world…</option>
                {activeWorlds
                  .filter((w) => !(w.id === sourceWorldId && w.currencySymbol === sourceCurrency))
                  .map((w) => (
                    <option key={w.id} value={w.id}>{w.name} — receive {w.currencySymbol}</option>
                  ))}
              </select>
              {destWorld && destRate && (
                <p className="text-[10px] text-slate-600 mt-1">Rate: 1 UKC = {(1 / destRate.ukcPerUnit).toFixed(6)} {destWorld.currencySymbol} &nbsp;|&nbsp; 1 {destWorld.currencySymbol} = {destRate.ukcPerUnit} UKC</p>
              )}
              {destWorldId && !destRate && (
                <p className="text-[10px] text-amber-600 mt-1">No exchange rate set for this world</p>
              )}
            </div>

            {/* Amount */}
            <div>
              <label className="block text-[10px] font-medium text-slate-500 uppercase tracking-wider mb-1.5">
                Amount ({sourceCurrency || '…'}){' '}
                {sourceBalance && <span className="text-slate-700 normal-case">— available: {parseFloat(sourceBalance.balance).toLocaleString(undefined, { maximumFractionDigits: 4 })} {sourceCurrency}</span>}
              </label>
              <input
                type="number"
                min="0.00000001"
                step="0.00000001"
                max={sourceBalance ? parseFloat(sourceBalance.balance) : undefined}
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                placeholder="e.g. 50.00"
                required
                className={inputCls}
              />
            </div>

            {/* Rate preview */}
            {previewAmount && sourceWorld && destWorld && (
              <motion.div
                initial={{ opacity: 0, y: -4 }}
                animate={{ opacity: 1, y: 0 }}
                className="p-3.5 rounded-xl bg-cyan-500/[0.05] border border-cyan-500/20 space-y-2"
              >
                <p className="text-[10px] font-semibold text-cyan-500 uppercase tracking-wider">Exchange Preview</p>
                <div className="flex items-center justify-between">
                  <div className="text-center">
                    <p className="font-mono text-sm font-bold text-white">{parseFloat(amount).toFixed(4)} {sourceCurrency}</p>
                    <p className="text-[10px] text-slate-600">{sourceWorld?.name ?? 'source'}</p>
                  </div>
                  <ArrowLeftRight className="w-4 h-4 text-cyan-500/60 flex-shrink-0" />
                  <div className="text-center">
                    <p className="font-mono text-sm font-bold text-emerald-400">{parseFloat(previewAmount).toFixed(4)} {destWorld.currencySymbol}</p>
                    <p className="text-[10px] text-slate-600">{destWorld.name}</p>
                  </div>
                </div>
                {sourceRate && destRate && (
                  <p className="text-[10px] text-slate-600 text-center">
                    Effective rate: ×{(sourceRate.ukcPerUnit / destRate.ukcPerUnit).toFixed(6)} &nbsp;({sourceWorld.currencySymbol}/{destWorld.currencySymbol})
                  </p>
                )}
              </motion.div>
            )}

            {mutation.isError && (
              <p className="text-red-400 text-xs bg-red-500/10 border border-red-500/20 rounded-xl px-3 py-2">
                {(mutation.error as { response?: { data?: { message?: string } } })?.response?.data?.message ?? 'Exchange failed.'}
              </p>
            )}

            <button
              type="submit"
              disabled={mutation.isPending || !sourceRate || !destRate}
              className="w-full btn-primary flex items-center justify-center gap-2 py-3 rounded-xl text-sm font-semibold disabled:opacity-40"
            >
              {mutation.isPending ? (
                <><Loader2 className="w-4 h-4 animate-spin" /> Processing…</>
              ) : (
                <><ArrowLeftRight className="w-4 h-4" /> Execute Exchange</>
              )}
            </button>
          </form>
        )}
      </div>

      <div className="glass-card p-4 space-y-2" style={{ borderColor: 'rgba(245,158,11,0.15)' }}>
        <p className="text-[10px] font-semibold text-amber-400 uppercase tracking-wider">How Exchange Rates Work</p>
        <p className="text-xs text-slate-500">
          Exchange rates are set by the Central Bank for each partner world. The conversion formula is:
          <span className="font-mono text-slate-400 ml-1">receive = send × (sourceRate / destRate)</span>.
          A world with a higher UKC-per-unit rate is more expensive to enter. All exchanges settle via the immutable ledger.
        </p>
      </div>

      <HolderWorldConvertSection holder={holder} holderId={holderId} />
    </div>
  );
}

/* ─── Tab: Reports ─── */
function ReportsTab({ holder }: { holder: Holder }) {
  const { data: balances = [], isLoading } = useQuery<HolderBalance[]>({
    queryKey: ['holder-balances', holder.id],
    queryFn: () => api.get(`/holders/${holder.id}/balances`).then((r) => r.data),
    refetchInterval: 30_000,
  });

  const { data: worlds = [] } = useQuery<World[]>({
    queryKey: ['worlds'],
    queryFn: () => api.get('/worlds').then((r) => r.data),
  });

  const worldMap = Object.fromEntries(worlds.map((w) => [w.id, w]));

  return (
    <div className="space-y-6">
      <div className="glass-card overflow-hidden">
        <div className="h-px bg-gradient-to-r from-transparent via-purple-500/25 to-transparent" />
        <div className="px-5 py-3.5 flex items-center justify-between border-b border-white/[0.04]">
          <p className="text-xs font-semibold text-slate-400 uppercase tracking-widest">Current Balances</p>
          <p className="text-[10px] text-slate-700">Auto-refreshes every 30s</p>
        </div>

        {isLoading ? (
          <div className="p-8 flex items-center gap-3 text-slate-600 text-xs">
            <Loader2 className="w-3.5 h-3.5 animate-spin" /> Loading balances…
          </div>
        ) : balances.length === 0 ? (
          <div className="p-10 text-center">
            <FileBarChart2 className="w-8 h-8 text-slate-700 mx-auto mb-3" />
            <p className="text-slate-500 text-sm">No balances yet</p>
            <p className="text-slate-700 text-xs mt-1">Fund this holder to see balance records</p>
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr style={{ borderBottom: '1px solid rgba(255,255,255,0.04)' }}>
                {['Currency', 'Partner World', 'Balance', 'Last Updated'].map((h) => (
                  <th key={h} className="text-left px-5 py-3 text-[10px] font-semibold uppercase tracking-widest text-slate-600">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {balances.map((b) => {
                const world = worldMap[b.worldId];
                return (
                  <tr key={`${b.worldId}-${b.currency}`} style={{ borderBottom: '1px solid rgba(255,255,255,0.03)' }}>
                    <td className="px-5 py-3.5">
                      <span className="font-mono text-sm text-slate-200 bg-white/[0.04] px-2 py-0.5 rounded">{b.currency}</span>
                    </td>
                    <td className="px-5 py-3.5">
                      {world ? (
                        <div>
                          <p className="text-sm font-medium text-slate-200">{world.name}</p>
                          <p className="font-mono text-[10px] text-slate-600">{b.worldId}</p>
                        </div>
                      ) : (
                        <span className="font-mono text-[10px] text-slate-600">{b.worldId}</span>
                      )}
                    </td>
                    <td className="px-5 py-3.5 font-mono text-sm text-emerald-400 font-semibold">
                      {parseFloat(b.balance).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 8 })}
                    </td>
                    <td className="px-5 py-3.5 text-xs text-slate-600">
                      {formatDateTime(b.updatedAt)}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {/* Summary totals */}
      {balances.length > 0 && (
        <div className="glass-card p-5 space-y-3">
          <p className="text-xs font-semibold text-slate-400 uppercase tracking-widest">Summary</p>
          <div className="grid grid-cols-2 gap-4 sm:grid-cols-3">
            <div>
              <p className="text-[10px] text-slate-600 mb-0.5">Total Positions</p>
              <p className="text-lg font-bold font-mono text-cyan-400">{balances.length}</p>
            </div>
            <div>
              <p className="text-[10px] text-slate-600 mb-0.5">Balances</p>
              <div className="space-y-0.5">
                {Object.entries(sumByCurrency(balances)).map(([currency, amount]) => (
                  <p key={currency} className="font-bold font-mono text-emerald-400">
                    {fmtAmount(amount)} <span className="text-xs text-slate-500">{currency}</span>
                  </p>
                ))}
              </div>
            </div>
            <div>
              <p className="text-[10px] text-slate-600 mb-0.5">Last Activity</p>
              <p className="text-sm font-mono text-slate-300">
                {formatDate(new Date(Math.max(...balances.map((b) => new Date(b.updatedAt).getTime()))).toISOString())}
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

/* ─── Tab: Dashboard ─── */
function DashboardTab({ holder }: { holder: Holder }) {
  const { data: balances = [] } = useQuery<HolderBalance[]>({
    queryKey: ['holder-balances', holder.id],
    queryFn: () => api.get(`/holders/${holder.id}/balances`).then((r) => r.data),
  });

  const chartData = balances.map((b) => ({
    name: b.currency,
    value: parseFloat(b.balance),
  }));

  const BAR_COLORS = ['#10b981', '#00d4ff', '#a855f7', '#f59e0b', '#ef4444'];

  const total = balances.reduce((acc, b) => acc + parseFloat(b.balance || '0'), 0);

  return (
    <div className="space-y-6">
      {/* Summary row */}
      <div className="grid grid-cols-2 gap-4 sm:grid-cols-3">
        <StatCard label="Total UKC" value={`${fmtAmount(sumByCurrency(balances)['UKC'] ?? 0)} UKC`} accent="emerald" />
        <StatCard label="Active Positions" value={String(balances.filter((b) => parseFloat(b.balance) > 0).length)} accent="cyan" />
        <StatCard label="Holder Status" value={holder.isActive ? 'Active' : 'Inactive'} accent={holder.isActive ? 'emerald' : 'purple'} />
      </div>

      {/* Bar chart */}
      <div className="glass-card p-6">
        <div className="h-px -mx-6 mb-6 bg-gradient-to-r from-transparent via-cyan-500/20 to-transparent" />
        <div className="flex items-center gap-2 mb-5">
          <BarChart3 className="w-3.5 h-3.5 text-cyan-400" />
          <p className="text-xs font-semibold text-slate-300">Balance by Currency</p>
        </div>

        {balances.length === 0 ? (
          <div className="h-48 flex flex-col items-center justify-center text-slate-700 gap-2">
            <BarChart3 className="w-8 h-8" />
            <p className="text-xs">No balance data yet — fund this holder to see charts</p>
          </div>
        ) : (
          <ResponsiveContainer width="100%" height={200}>
            <BarChart data={chartData} barSize={40}>
              <CartesianGrid vertical={false} stroke="rgba(255,255,255,0.04)" />
              <XAxis dataKey="name" tick={{ fill: '#64748b', fontSize: 11 }} axisLine={false} tickLine={false} />
              <YAxis tick={{ fill: '#64748b', fontSize: 10 }} axisLine={false} tickLine={false}
                tickFormatter={(v) => v >= 1e6 ? `${(v / 1e6).toFixed(1)}M` : v >= 1e3 ? `${(v / 1e3).toFixed(1)}K` : String(v)} />
              <Tooltip contentStyle={TOOLTIP_STYLE} cursor={{ fill: 'rgba(255,255,255,0.03)' }}
                formatter={(v: number) => [v.toLocaleString(undefined, { maximumFractionDigits: 4 }), 'Balance']} />
              <Bar dataKey="value" radius={[6, 6, 0, 0]}>
                {chartData.map((_, i) => (
                  <Cell key={i} fill={BAR_COLORS[i % BAR_COLORS.length]} fillOpacity={0.85} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        )}
      </div>

      {/* Distribution table */}
      {total > 0 && (
        <div className="glass-card p-5 space-y-3">
          <p className="text-xs font-semibold text-slate-400 uppercase tracking-widest">Portfolio Share</p>
          <div className="space-y-2.5">
            {balances
              .filter((b) => parseFloat(b.balance) > 0)
              .sort((a, b) => parseFloat(b.balance) - parseFloat(a.balance))
              .map((b, i) => {
                const pct = total > 0 ? (parseFloat(b.balance) / total) * 100 : 0;
                return (
                  <div key={`${b.worldId}-${b.currency}`}>
                    <div className="flex items-center justify-between mb-1">
                      <span className="font-mono text-xs text-slate-400">{b.currency}</span>
                      <span className="text-xs text-slate-500">{pct.toFixed(1)}%</span>
                    </div>
                    <div className="h-1.5 bg-white/[0.05] rounded-full overflow-hidden">
                      <motion.div
                        initial={{ width: 0 }}
                        animate={{ width: `${pct}%` }}
                        transition={{ duration: 0.6, delay: i * 0.1, ease: 'easeOut' }}
                        className="h-full rounded-full"
                        style={{ background: BAR_COLORS[i % BAR_COLORS.length] }}
                      />
                    </div>
                  </div>
                );
              })}
          </div>
        </div>
      )}
    </div>
  );
}

/* ─── Main Page ─── */
const TABS: { id: Tab; label: string; icon: React.ElementType }[] = [
  { id: 'overview',   label: 'Overview',              icon: LayoutDashboard },
  { id: 'convert',    label: 'Convert World Credits',  icon: ArrowLeftRight  },
  { id: 'reports',    label: 'Reports',                icon: FileBarChart2   },
  { id: 'dashboard',  label: 'Dashboard',              icon: BarChart3       },
];

export function HolderDetailPage() {
  const { holderId } = useParams<{ holderId: string }>();
  const [activeTab, setActiveTab] = useState<Tab>('overview');
  const [showEdit, setShowEdit] = useState(false);
  const [showReset, setShowReset] = useState(false);
  const qc = useQueryClient();

  const { data: holder, isLoading } = useQuery<Holder>({
    queryKey: ['holder', holderId],
    queryFn: () => api.get(`/holders/${holderId}`).then((r) => r.data),
    enabled: !!holderId,
  });

  const toggleMutation = useMutation({
    mutationFn: (isActive: boolean) => api.patch(`/holders/${holderId}`, { isActive }).then((r) => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['holder', holderId] }),
  });

  if (isLoading) {
    return (
      <div className="p-8 flex items-center gap-3 text-slate-500">
        <Loader2 className="w-4 h-4 animate-spin" /> Loading holder…
      </div>
    );
  }

  if (!holder) {
    return <div className="p-8 text-red-400">Holder not found</div>;
  }

  return (
    <div className="p-8 space-y-6">
      {showEdit && (
        <EditHolderModal
          holder={holder}
          onClose={() => setShowEdit(false)}
          onSaved={() => {}}
        />
      )}
      {showReset && <ResetPasswordModal holder={holder} onClose={() => setShowReset(false)} />}

      {/* Header */}
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div className="flex items-center gap-4">
          <Link to="/holders" className="text-slate-600 hover:text-slate-300 transition-colors">
            <ChevronLeft className="w-5 h-5" />
          </Link>
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-emerald-500/15 to-cyan-500/15 border border-emerald-500/20 flex items-center justify-center">
              <Wallet className="w-5 h-5 text-emerald-400" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-white">{holder.name}</h2>
              <p className="text-xs text-slate-600">{holder.email}</p>
            </div>
          </div>
          <span className={`inline-flex items-center gap-1.5 text-xs px-2.5 py-1 rounded-full font-medium ${holder.isActive ? 'badge-active' : 'badge-inactive'}`}>
            <span className={`w-1.5 h-1.5 rounded-full ${holder.isActive ? 'bg-emerald-400 pulse-dot' : 'bg-slate-500'}`} />
            {holder.isActive ? 'Active' : 'Inactive'}
          </span>
        </div>

        {/* Action buttons */}
        <div className="flex items-center gap-2">
          <button
            onClick={() => setShowEdit(true)}
            className="flex items-center gap-1.5 px-3 py-2 rounded-xl text-xs font-medium text-slate-300 border border-white/10 hover:border-cyan-500/30 hover:text-cyan-400 transition-all"
          >
            <Pencil className="w-3.5 h-3.5" />
            Edit
          </button>
          <button
            onClick={() => setShowReset(true)}
            className="flex items-center gap-1.5 px-3 py-2 rounded-xl text-xs font-medium text-slate-300 border border-white/10 hover:border-amber-500/30 hover:text-amber-400 transition-all"
          >
            <KeyRound className="w-3.5 h-3.5" />
            Reset Password
          </button>
          <button
            onClick={() => toggleMutation.mutate(!holder.isActive)}
            disabled={toggleMutation.isPending}
            className={`flex items-center gap-1.5 px-3 py-2 rounded-xl text-xs font-medium border transition-all ${
              holder.isActive
                ? 'text-red-400 border-red-500/20 hover:border-red-500/40 hover:bg-red-500/10'
                : 'text-emerald-400 border-emerald-500/20 hover:border-emerald-500/40 hover:bg-emerald-500/10'
            }`}
          >
            {toggleMutation.isPending ? (
              <Loader2 className="w-3.5 h-3.5 animate-spin" />
            ) : holder.isActive ? (
              <><ToggleRight className="w-4 h-4" /> Deactivate</>
            ) : (
              <><ToggleLeft className="w-4 h-4" /> Activate</>
            )}
          </button>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 p-1 rounded-xl bg-white/[0.03] border border-white/[0.06] w-fit">
        {TABS.map(({ id, label, icon: Icon }) => (
          <button
            key={id}
            onClick={() => setActiveTab(id)}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg text-xs font-medium transition-all ${
              activeTab === id
                ? 'bg-white/[0.08] text-white shadow-sm'
                : 'text-slate-500 hover:text-slate-300'
            }`}
          >
            <Icon className="w-3.5 h-3.5" />
            {label}
          </button>
        ))}
      </div>

      {/* Tab content */}
      <motion.div
        key={activeTab}
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.25 }}
      >
        {activeTab === 'overview'  && <OverviewTab holder={holder} />}
        {activeTab === 'convert'   && <ConvertTab  holder={holder} holderId={holderId!} />}
        {activeTab === 'reports'   && <ReportsTab  holder={holder} />}
        {activeTab === 'dashboard' && <DashboardTab holder={holder} />}
      </motion.div>
    </div>
  );
}
